/**
 * Card-network branding for saved payment methods.
 *
 * The brand string varies by processor, and this component is shared across
 * all three checkouts:
 *
 *   Authorize.Net  "Visa"  "MasterCard"  "AmericanExpress"  "Discover"
 *   Stripe         "visa"  "mastercard"  "amex"             "discover"
 *   Square         "VISA"  "MASTERCARD"  "AMERICAN_EXPRESS" "DISCOVER"
 *
 * Normalising to letters-only lowercase collapses all three, so a processor
 * swap doesn't silently drop the logo.
 */

const BRANDS = {
  visa: {
    label: "Visa",
    src: "/images/payment/visa.webp",
    // The Visa asset is dark artwork with a transparent background; without a
    // white plate it disappears against a dark row. Same treatment the footer
    // gives it.
    needsPlate: true,
  },
  mastercard: { label: "Mastercard", src: "/images/payment/mastercard.svg" },
  amex: { label: "American Express", src: "/images/payment/amex.svg" },
  discover: { label: "Discover", src: "/images/payment/discover.png" },
};

// Everything that should resolve to one of the four assets we actually have.
const ALIASES = {
  visa: "visa",
  mastercard: "mastercard",
  mc: "mastercard",
  amex: "amex",
  americanexpress: "amex",
  discover: "discover",
};

/**
 * Resolve a processor's brand string to display info.
 *
 * Returns null for brands we hold no artwork for (JCB, Diners Club, UnionPay),
 * so callers fall back to text rather than rendering a broken image.
 */
export function cardBrandInfo(brand) {
  if (!brand) return null;
  const key = String(brand).toLowerCase().replace(/[^a-z]/g, "");
  const resolved = ALIASES[key];
  return resolved ? BRANDS[resolved] : null;
}

/** Human-readable brand name, falling back to the processor's own string. */
export function cardBrandLabel(brand) {
  const info = cardBrandInfo(brand);
  if (info) return info.label;
  return brand ? String(brand).toUpperCase() : "Card";
}
